import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const num = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// Compute grade from % using the meta grade bands (client-side live preview).
const gradeFrom = (pct, bands, fail) => {
  if (pct == null || !bands) return null;
  for (const b of bands) if (pct >= b.min) return b.grade;
  return fail || "D";
};

export default function PrePrimarySats({ cls, section }) {
  const [mode, setMode] = useState("entry");
  const [meta, setMeta] = useState(null);
  const [exam, setExam] = useState("FA1");
  const [rows, setRows] = useState([]);
  const [colMax, setColMax] = useState(25);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(() => new Set());
  const [student, setStudent] = useState("");
  const [card, setCard] = useState(null);
  const [pdf, setPdf] = useState(false);

  const subjects = meta?.subjects || [];
  const activities = meta?.activities || [];
  const gradeOpts = meta?.grade_options || [];

  useEffect(() => {
    if (!cls) return;
    api.get("/pp-sats/meta", { params: { class_name: cls } })
      .then(({ data }) => setMeta(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load SATS config"));
  }, [cls]);

  const load = useCallback(() => {
    if (!cls || !section || !exam) { setRows([]); return; }
    setLoading(true);
    api.get("/pp-sats/entry", { params: { class_name: cls, section, exam_key: exam } })
      .then(({ data }) => {
        setColMax(data.col_max || 25);
        // shape each row into editable cells
        setRows((data.students || []).map((s) => ({
          student_id: s.student_id, name: s.name, roll_number: s.roll_number,
          subjects: Object.fromEntries((data.subjects || []).map((sub) => {
            const c = s.subjects?.[sub] || {};
            return [sub, { o: c.o ?? "", d: c.d ?? "", w: c.w ?? "" }];
          })),
          activities: Object.fromEntries((data.activities || []).map((a) => [a, s.activities?.[a] ?? ""])),
          remark: s.remark || "",
        })));
        setDirty(new Set());
      })
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load SATS marks"))
      .finally(() => setLoading(false));
  }, [cls, section, exam]);
  useEffect(() => { load(); }, [load]);

  const touch = (sid) => setDirty((d) => new Set(d).add(sid));

  const setSub = (sid, sub, field, value) => {
    const clean = value.replace(/[^0-9.]/g, "");
    setRows((rs) => rs.map((r) => r.student_id === sid
      ? { ...r, subjects: { ...r.subjects, [sub]: { ...r.subjects[sub], [field]: clean } } } : r));
    touch(sid);
  };
  const setAct = (sid, act, value) => {
    setRows((rs) => rs.map((r) => r.student_id === sid
      ? { ...r, activities: { ...r.activities, [act]: value } } : r));
    touch(sid);
  };
  const setRemark = (sid, value) => {
    setRows((rs) => rs.map((r) => r.student_id === sid ? { ...r, remark: value } : r));
    touch(sid);
  };

  const rowCalc = (r) => {
    let gt = 0, has = false;
    const per = {};
    for (const sub of subjects) {
      const c = r.subjects[sub] || {};
      const o = num(c.o), d = num(c.d), w = num(c.w);
      if (o == null && d == null && w == null) { per[sub] = { t: null, g: null }; continue; }
      const t = (o || 0) + (d || 0) + (w || 0);
      const pct = colMax ? (t * 100) / colMax : 0;
      per[sub] = { t, g: gradeFrom(Math.round(pct * 100) / 100, meta?.grade_bands, meta?.fail_grade) };
      gt += t; has = true;
    }
    const gmax = colMax * subjects.length;
    const pct = has && gmax ? Math.round((gt * 100 / gmax) * 100) / 100 : null;
    return { per, gt: has ? gt : null, gmax, pct, overall: pct == null ? null : gradeFrom(pct, meta?.grade_bands, meta?.fail_grade) };
  };

  const save = async () => {
    const students = rows.filter((r) => dirty.has(r.student_id)).map((r) => ({
      student_id: r.student_id,
      subjects: Object.fromEntries(subjects.map((sub) => {
        const c = r.subjects[sub] || {};
        return [sub, { o: num(c.o), d: num(c.d), w: num(c.w) }];
      })),
      activities: Object.fromEntries(activities.map((a) => [a, r.activities[a] || null])),
      remark: r.remark || "",
    }));
    if (!students.length) { toast.message("Nothing to save."); return; }
    setSaving(true);
    try {
      await api.put("/pp-sats/entry", { class_name: cls, section, exam_key: exam, students });
      toast.success("SATS marks saved.");
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const loadCard = (sid) => {
    if (!sid) { setCard(null); return; }
    api.get("/pp-sats/card", { params: { class_name: cls, section, student_id: sid, exam_key: exam } })
      .then(({ data }) => setCard(data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail) || "Could not load card"));
  };

  const downloadPdf = async () => {
    if (!student) { toast.message("Pick a student first."); return; }
    setPdf(true);
    try {
      const res = await api.get("/pp-sats/card/pdf", {
        params: { class_name: cls, section, student_id: student, exam_key: exam }, responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(cd);
      const fname = m ? decodeURIComponent(m[1]) : "sats-report.pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not generate PDF");
    } finally { setPdf(false); }
  };

  const exams = meta?.exams || [
    { key: "FA1", label: "FA-1" }, { key: "FA2", label: "FA-2" }, { key: "MIDTERM", label: "MID TERM" },
    { key: "FA3", label: "FA-3" }, { key: "FA4", label: "FA-4" }, { key: "ANNUAL", label: "ANNUAL" },
  ];

  return (
    <div className="space-y-4" data-testid="pp-sats">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5">
          <button onClick={() => setMode("entry")} className={`px-3 py-1.5 text-sm rounded-md ${mode === "entry" ? "bg-indigo-600 text-white" : "text-slate-600 dark:text-slate-300"}`} data-testid="ppsats-mode-entry">Enter marks</button>
          <button onClick={() => setMode("report")} className={`px-3 py-1.5 text-sm rounded-md ${mode === "report" ? "bg-indigo-600 text-white" : "text-slate-600 dark:text-slate-300"}`} data-testid="ppsats-mode-report">Report cards</button>
        </div>
        <div className="ml-auto">
          <Select value={exam} onValueChange={setExam}>
            <SelectTrigger className="w-[150px]" data-testid="ppsats-exam-select"><SelectValue /></SelectTrigger>
            <SelectContent>{exams.map((e) => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400">
        Pre-primary SATS · each subject: <b>Oral (O) · Dictation (D) · Written (W)</b>, <b>T = O+D+W</b> (out of {colMax}), grade from T%. Activities &amp; Remark are entered manually.
      </div>

      {mode === "entry" ? (
        <>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving || dirty.size === 0} className="h-10 bg-indigo-600 hover:bg-indigo-700" data-testid="ppsats-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save{dirty.size ? ` (${dirty.size})` : ""}
            </Button>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-x-auto" data-testid="ppsats-entry-grid">
            {loading ? <div className="p-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              : rows.length === 0 ? <div className="p-10 text-center text-slate-400">No students in this class/section.</div>
              : (
                <table className="text-sm border-collapse">
                  <thead className="bg-slate-50 dark:bg-slate-900/40 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th rowSpan={2} className="px-3 py-2 text-left sticky left-0 bg-slate-50 dark:bg-slate-900/40 z-10 min-w-[150px]">Student</th>
                      {subjects.map((sub) => <th key={sub} colSpan={5} className="px-2 py-2 text-center border-l border-slate-200 dark:border-slate-700">{sub}</th>)}
                      <th colSpan={3} className="px-2 py-2 text-center border-l border-slate-200 dark:border-slate-700">Grand</th>
                      {activities.map((a) => <th key={a} rowSpan={2} className="px-2 py-2 text-center border-l border-slate-200 dark:border-slate-700 whitespace-nowrap">{a}</th>)}
                      <th rowSpan={2} className="px-2 py-2 text-center border-l border-slate-200 dark:border-slate-700 min-w-[140px]">Remark</th>
                    </tr>
                    <tr>
                      {subjects.map((sub) => (
                        <>
                          <th key={`${sub}-o`} className="px-1.5 py-1 text-center border-l border-slate-200 dark:border-slate-700">O</th>
                          <th key={`${sub}-d`} className="px-1.5 py-1 text-center">D</th>
                          <th key={`${sub}-w`} className="px-1.5 py-1 text-center">W</th>
                          <th key={`${sub}-t`} className="px-1.5 py-1 text-center">T</th>
                          <th key={`${sub}-g`} className="px-1.5 py-1 text-center">G</th>
                        </>
                      ))}
                      <th className="px-2 py-1 text-center border-l border-slate-200 dark:border-slate-700">Total</th>
                      <th className="px-2 py-1 text-center">%</th>
                      <th className="px-2 py-1 text-center">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const calc = rowCalc(r);
                      return (
                        <tr key={r.student_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`ppsats-row-${r.roll_number}`}>
                          <td className="px-3 py-2 sticky left-0 bg-white dark:bg-slate-900 z-10">
                            <div className="font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">{r.name}</div>
                            <div className="text-[10px] text-slate-400 uppercase">{r.roll_number}</div>
                          </td>
                          {subjects.map((sub) => {
                            const c = r.subjects[sub] || {};
                            const pc = calc.per[sub] || {};
                            return (
                              <>
                                <td key={`${sub}-o`} className="px-1 py-1.5 border-l border-slate-100 dark:border-slate-800"><Input value={c.o} onChange={(e) => setSub(r.student_id, sub, "o", e.target.value)} inputMode="decimal" className="h-8 w-12 text-center px-1" data-testid={`ppsats-${r.student_id}-${sub}-o`} /></td>
                                <td key={`${sub}-d`} className="px-1 py-1.5"><Input value={c.d} onChange={(e) => setSub(r.student_id, sub, "d", e.target.value)} inputMode="decimal" className="h-8 w-12 text-center px-1" data-testid={`ppsats-${r.student_id}-${sub}-d`} /></td>
                                <td key={`${sub}-w`} className="px-1 py-1.5"><Input value={c.w} onChange={(e) => setSub(r.student_id, sub, "w", e.target.value)} inputMode="decimal" className="h-8 w-12 text-center px-1" data-testid={`ppsats-${r.student_id}-${sub}-w`} /></td>
                                <td key={`${sub}-t`} className="px-1 py-1.5 text-center font-semibold text-indigo-700 dark:text-indigo-300">{pc.t ?? "—"}</td>
                                <td key={`${sub}-g`} className="px-1 py-1.5 text-center font-semibold">{pc.g ?? "—"}</td>
                              </>
                            );
                          })}
                          <td className="px-2 py-1.5 text-center font-semibold border-l border-slate-100 dark:border-slate-800">{calc.gt ?? "—"}</td>
                          <td className="px-2 py-1.5 text-center">{calc.pct != null ? `${calc.pct}%` : "—"}</td>
                          <td className="px-2 py-1.5 text-center font-semibold text-emerald-700 dark:text-emerald-300">{calc.overall ?? "—"}</td>
                          {activities.map((a) => (
                            <td key={a} className="px-1 py-1.5 border-l border-slate-100 dark:border-slate-800">
                              <Select value={r.activities[a] || "__none"} onValueChange={(v) => setAct(r.student_id, a, v === "__none" ? "" : v)}>
                                <SelectTrigger className="h-8 w-16 mx-auto text-center px-1" data-testid={`ppsats-${r.student_id}-act-${a}`}><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__none">—</SelectItem>
                                  {gradeOpts.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                          ))}
                          <td className="px-1 py-1.5 border-l border-slate-100 dark:border-slate-800"><Input value={r.remark} onChange={(e) => setRemark(r.student_id, e.target.value)} placeholder="Remark" className="h-8 w-36 px-2" data-testid={`ppsats-${r.student_id}-remark`} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">Student</label>
              <Select value={student} onValueChange={(v) => { setStudent(v); loadCard(v); }}>
                <SelectTrigger className="w-[240px]" data-testid="ppsats-student-select"><SelectValue placeholder="Select a student…" /></SelectTrigger>
                <SelectContent>{rows.map((r) => <SelectItem key={r.student_id} value={r.student_id}>{r.roll_number} · {r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={downloadPdf} disabled={pdf || !student} className="h-10 bg-indigo-600 hover:bg-indigo-700" data-testid="ppsats-pdf-btn">
              {pdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download PDF
            </Button>
          </div>
          {!card ? (
            <div className="py-14 text-center text-slate-400">Pick a student to view their SATS report card.</div>
          ) : (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="ppsats-card">
              <div className="text-center border-b border-slate-100 dark:border-slate-800 pb-3 mb-4">
                <div className="text-xl font-bold text-indigo-900 dark:text-indigo-300" style={{ fontFamily: "Outfit" }}>{card.school_name}</div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-1">SATS Progress Report</div>
                <div className="text-sm text-slate-600 dark:text-slate-300 mt-1.5">
                  <b>Name:</b> {card.student_name} · <b>Class &amp; Section:</b> {card.class_name}-{card.section} · <b>Roll No:</b> {card.roll_number} · <b>Assessment:</b> {card.exam_label}
                </div>
              </div>
              <table className="w-full text-sm mb-4">
                <thead className="bg-indigo-600 text-white">
                  <tr>
                    <th className="px-2 py-2 text-left">Subject</th>
                    <th className="px-2 py-2 text-center">Oral (O)</th>
                    <th className="px-2 py-2 text-center">Dictation (D)</th>
                    <th className="px-2 py-2 text-center">Written (W)</th>
                    <th className="px-2 py-2 text-center">Total (T) /{card.col_max}</th>
                    <th className="px-2 py-2 text-center">Grade (G)</th>
                  </tr>
                </thead>
                <tbody>
                  {(card.subjects || []).map((s, i) => (
                    <tr key={s.subject} className={i % 2 ? "bg-indigo-50/50 dark:bg-indigo-900/10" : ""}>
                      <td className="px-2 py-1.5 font-semibold">{s.subject}</td>
                      <td className="px-2 py-1.5 text-center">{s.o ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center">{s.d ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center">{s.w ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center font-semibold text-indigo-700 dark:text-indigo-300">{s.t ?? "—"}</td>
                      <td className="px-2 py-1.5 text-center font-semibold">{s.grade ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="grid grid-cols-3 gap-3 mb-4 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 p-3 text-sm">
                <div><div className="text-slate-500 text-xs">Grand Total</div><div className="font-bold">{card.grand_total ?? "—"} / {card.grand_max}</div></div>
                <div><div className="text-slate-500 text-xs">Percentage</div><div className="font-bold">{card.percentage != null ? `${card.percentage}%` : "—"}</div></div>
                <div><div className="text-slate-500 text-xs">Overall Grade</div><div className="font-bold">{card.overall_grade ?? "—"}</div></div>
              </div>
              <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 p-3 mb-3">
                <div className="text-purple-800 dark:text-purple-300 font-semibold text-sm mb-2">Activities</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  {(card.activities || []).map((a) => <div key={a.name}><b>{a.name}:</b> {a.grade || "—"}</div>)}
                </div>
              </div>
              <div className="text-sm"><b>Remark:</b> {card.remark || "—"}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
